import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import './Login.css';

export default function Login() {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [countdown, setCountdown] = useState('');

  // Countdown timer for lockout
  useEffect(() => {
    if (!lockoutUntil) return;

    const interval = setInterval(() => {
      const remaining = new Date(lockoutUntil) - new Date();
      if (remaining <= 0) {
        setLockoutUntil(null);
        setCountdown('');
        setError('');
        setRemainingAttempts(null);
        clearInterval(interval);
      } else {
        const hours = Math.floor(remaining / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        setCountdown(
          hours > 0
            ? `${hours}h ${minutes}m ${seconds}s`
            : `${minutes}m ${seconds}s`
        );
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // only clear error, never clear remainingAttempts on typing
    if (error && !lockoutUntil) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.username.trim() || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      const data = await authAPI.login(formData.username.trim(), formData.password);

      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user.role === 'admin') window.location.href = '/admin';
        else if (data.user.role === 'reseller') window.location.href = '/reseller';
        else window.location.href = '/user';
      } else {
        if (data.locked) {
          setLockoutUntil(data.lockout_until);
          setRemainingAttempts(0);
          setError('');
        } else {
          if (data.remaining_attempts !== undefined) {
            setRemainingAttempts(data.remaining_attempts);
          }
          setError(data.message || 'Login failed');
        }
      }
    } catch (err) {
      if (err.message && err.message.includes('fetch')) {
        setError('Unable to connect to server. Please check your internet connection.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  const [showPassword, setShowPassword] = useState(false);
  const isLocked = lockoutUntil && new Date(lockoutUntil) > new Date();

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">🐯</div>
        <h2>Tiger Guard Login</h2>
        <p className="login-subtitle">Welcome back! Please login to your account.</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text" id="username" name="username"
              value={formData.username} onChange={handleChange}
              placeholder="Enter your username"
              autoComplete="username"
              disabled={loading || isLocked} required
            />
          </div>




        <div className="form-group password-group">
          <label htmlFor="password">Password</label>

          <div className="password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={loading || isLocked}
              required
            />

            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading || isLocked}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>
        </div>

          {/* General error - shows first */}
          {error && !isLocked && (
            <div className="error-message">
              <span className="error-icon">⚠️</span> {error}
            </div>
          )}

          {/* Remaining attempts warning - shows below error */}
          {remainingAttempts !== null && remainingAttempts > 0 && !isLocked && (
            <div className="attempts-warning">
              ⚠️ {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining before account is locked
            </div>
          )}

          {/* Lockout message with countdown */}
          {isLocked && (
            <div className="lockout-message">
              🔒 Account temporarily locked<br />
              <span className="countdown">Try again in <strong>{countdown}</strong></span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || isLocked}
            className="login-button"
          >
            {loading
              ? <><span className="spinner"></span> Logging in...</>
              : isLocked
                ? '🔒 Account Locked'
                : 'Login'
            }
          </button>
        </form>

        <div className="login-footer">
          <p className="footer-links">
            <a href="/register">Create an account</a>
            <span> · </span>
            <a href="/forgot-password">Forgot password?</a>
          </p>
          <p className="footer-text">🔒 Secure access to Tiger Guard platform</p>
        </div>
      </div>
    </div>
  );
}